import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { endOfDay, format, parseISO, startOfDay, subMonths } from "date-fns";
import {
  DELETIONS_PAGE_SIZE,
  DeletionAuditRow,
  useAdminOrganizations,
  useDeletions,
} from "lib/adminQueries";
import DateRangeDropdown from "../lpiReports/DateRangeDropdown";
import "./AdminDeletions.scss";

/** Inclusive day bounds, sent as full ISO instants so a same-day `to` still matches. */
const isoFrom = (d: Date | null): string | undefined =>
  d ? startOfDay(d).toISOString() : undefined;
const isoTo = (d: Date | null): string | undefined =>
  d ? endOfDay(d).toISOString() : undefined;

/** Who ran the deletion: the email when the token carried one, else the sub. */
const actorOf = (row: DeletionAuditRow): string =>
  row.deletedByEmail ?? row.deletedByUserId ?? "—";

/**
 * The deletion audit trail: every per-file deletion, across every tenant,
 * newest first. Answers "who deleted our data?", so it is super_admin-only
 * (enforced by the route gate and by the endpoint itself).
 */
const AdminDeletions: FC = () => {
  const { t } = useTranslation();

  // Draft dates move with the picker; only Apply commits them, so dragging a
  // range does not fire a fetch per click (same contract as UploadHistoryTab).
  const [startDate, setStartDate] = useState<Date | null>(subMonths(new Date(), 6));
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [committedStart, setCommittedStart] = useState<Date | null>(subMonths(new Date(), 6));
  const [committedEnd, setCommittedEnd] = useState<Date | null>(new Date());
  const [organizationId, setOrganizationId] = useState("");
  const [page, setPage] = useState(1);

  const orgsQuery = useAdminOrganizations();
  // An unreachable /admin/organizations must not take the page down: with no
  // list we simply know no names, and every tenant cell falls back to its id.
  const orgsData = orgsQuery.data;
  const organizations = useMemo(() => orgsData ?? [], [orgsData]);

  const orgNames = useMemo(() => {
    const map = new Map<string, string>();
    organizations.forEach((o) => map.set(o.id, o.name));
    return map;
  }, [organizations]);

  const query = useMemo(
    () => ({
      organizationId: organizationId || undefined,
      from: isoFrom(committedStart),
      to: isoTo(committedEnd),
      limit: DELETIONS_PAGE_SIZE,
      offset: (page - 1) * DELETIONS_PAGE_SIZE,
    }),
    [organizationId, committedStart, committedEnd, page]
  );

  const { data, isLoading, isError } = useDeletions(query);

  const rows = data?.deletions ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / DELETIONS_PAGE_SIZE);

  const applyDates = (): void => {
    setCommittedStart(startDate);
    setCommittedEnd(endDate);
    setPage(1);
  };

  return (
    <div className="admin-deletions">
      <div className="admin-deletions__header">
        <h1>{t("adminDeletions.title")}</h1>
        <p className="admin-deletions__subtitle">{t("adminDeletions.intro")}</p>
      </div>

      <div className="admin-deletions__controls">
        <div className="admin-deletions__filter-row">
          <label className="admin-deletions__tenant" htmlFor="admin-deletions-tenant">
            <span className="admin-deletions__tenant-label">
              {t("adminDeletions.tenant")}
            </span>
            <select
              id="admin-deletions-tenant"
              className="admin-deletions__tenant-select"
              value={organizationId}
              onChange={(e) => {
                setOrganizationId(e.target.value);
                setPage(1);
              }}
            >
              <option value="">{t("adminDeletions.allTenants")}</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </label>

          <DateRangeDropdown
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <button
            type="button"
            className="btn btn-primary admin-deletions__apply-btn"
            onClick={applyDates}
          >
            {t("Apply")}
          </button>
        </div>
      </div>

      <div className="admin-deletions__card">
        <p className="admin-deletions__result-count">
          <strong>{total.toLocaleString()}</strong>{" "}
          {t("adminDeletions.matchingFilters")}
        </p>

        {isError && (
          <div className="admin-deletions__error">
            {t("adminDeletions.loadError")}
          </div>
        )}

        {!isError && (
          <>
            <div
              className={`admin-deletions__table-wrap${
                isLoading ? " admin-deletions__table-wrap--loading" : ""
              }`}
            >
              {isLoading && (
                <div className="admin-deletions__loading-overlay">
                  <span>{t("common.loading")}</span>
                </div>
              )}
              <table className="admin-deletions__table">
                <thead>
                  <tr>
                    <th>{t("adminDeletions.col.deletedAt")}</th>
                    <th>{t("adminDeletions.col.tenant")}</th>
                    <th>{t("adminDeletions.col.filename")}</th>
                    <th>{t("adminDeletions.col.fileType")}</th>
                    <th>{t("adminDeletions.col.rowsDeleted")}</th>
                    <th>{t("adminDeletions.col.deletedBy")}</th>
                  </tr>
                </thead>
                <tbody>
                  {!isLoading && rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="admin-deletions__empty">
                        {t("adminDeletions.empty")}
                      </td>
                    </tr>
                  )}
                  {rows.map((row) => (
                    <tr key={row.deletionAuditId}>
                      <td>
                        <div className="admin-deletions__date-main">
                          {format(parseISO(row.deletedAt), "d MMM yyyy")}
                        </div>
                        <div className="admin-deletions__date-sub">
                          {format(parseISO(row.deletedAt), "HH:mm")}
                        </div>
                      </td>
                      <td className="admin-deletions__tenant-cell">
                        {orgNames.get(row.organizationId) ?? row.organizationId}
                      </td>
                      <td>
                        <div
                          className="admin-deletions__filename"
                          title={row.filename}
                        >
                          {row.filename}
                        </div>
                      </td>
                      <td>
                        <span className="admin-deletions__badge">
                          {row.fileType}
                        </span>
                      </td>
                      <td className="admin-deletions__rows">
                        {row.totalRowsDeleted.toLocaleString()}
                      </td>
                      <td className="admin-deletions__actor">{actorOf(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="admin-deletions__pagination">
              {totalPages > 1 && (
                <>
                  <span className="admin-deletions__page-info">
                    {t("Showing")} {(page - 1) * DELETIONS_PAGE_SIZE + 1}–
                    {Math.min(page * DELETIONS_PAGE_SIZE, total)} {t("of")}{" "}
                    {total}
                  </span>
                  <button
                    type="button"
                    className="admin-deletions__page-btn"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    ←
                  </button>
                  {(() => {
                    const windowSize = 5;
                    const halfWindow = Math.floor(windowSize / 2);
                    let startPage = Math.max(1, page - halfWindow);
                    const endPage = Math.min(totalPages, startPage + windowSize - 1);
                    if (endPage - startPage + 1 < windowSize) {
                      startPage = Math.max(1, endPage - windowSize + 1);
                    }
                    const pageNumbers = Array.from(
                      { length: endPage - startPage + 1 },
                      (_, i) => startPage + i
                    );
                    return pageNumbers.map((pageNum) => (
                      <button
                        key={pageNum}
                        type="button"
                        className={`admin-deletions__page-btn ${
                          page === pageNum ? "admin-deletions__page-btn--active" : ""
                        }`}
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    ));
                  })()}
                  <button
                    type="button"
                    className="admin-deletions__page-btn"
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    →
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDeletions;
